/**
 * Material Design 3 Theme Configuration
 * 
 * Colors are from the Material Theme Builder (https://m3.material.io/theme-builder).
 * To update: re-export the JSON, paste the light/dark schemes below, done.
 */

import { createTheme } from '@mui/material/styles';
import type { ThemeOptions } from '@mui/material/styles';
import m3_palette from './M3.json';

// ---------------------------------------------------------------------------
// Augment MUI palette with custom M3 tokens so they resolve in the sx prop
// ---------------------------------------------------------------------------

declare module '@mui/material/styles' {
  interface Palette {
    // Surface containers
    surfaceContainerLowest: string;
    surfaceContainerLow: string;
    surfaceContainer: string;
    surfaceContainerHigh: string;
    surfaceContainerHighest: string;
    surfaceDim: string;
    surfaceBright: string;
    surfaceTint: string;
    surfaceVariant: string;
    onSurfaceVariant: string;
    // Primary tokens
    primaryContainer: string;
    onPrimaryContainer: string;
    // Secondary tokens
    secondaryContainer: string;
    onSecondaryContainer: string;
    // Tertiary tokens
    tertiary: string;
    onTertiary: string;
    tertiaryContainer: string;
    onTertiaryContainer: string;
    // Error tokens
    errorContainer: string;
    onErrorContainer: string;
    // Outline tokens
    outline: string;
    outlineVariant: string;
    // Inverse tokens
    inverseSurface: string;
    inverseOnSurface: string;
    inversePrimary: string;
    // Additional tokens
    shadow: string;
    scrim: string;
    onBackground: string;
    // Fixed color tokens
    primaryFixed: string;
    onPrimaryFixed: string;
    primaryFixedDim: string;
    onPrimaryFixedVariant: string;
    secondaryFixed: string;
    onSecondaryFixed: string;
    secondaryFixedDim: string;
    onSecondaryFixedVariant: string;
    tertiaryFixed: string;
    onTertiaryFixed: string;
    tertiaryFixedDim: string;
    onTertiaryFixedVariant: string;
  }
  interface PaletteOptions {
    // Surface containers
    surfaceContainerLowest?: string;
    surfaceContainerLow?: string;
    surfaceContainer?: string;
    surfaceContainerHigh?: string;
    surfaceContainerHighest?: string;
    surfaceDim?: string;
    surfaceBright?: string;
    surfaceTint?: string;
    surfaceVariant?: string;
    onSurfaceVariant?: string;
    // Primary tokens
    primaryContainer?: string;
    onPrimaryContainer?: string;
    // Secondary tokens
    secondaryContainer?: string;
    onSecondaryContainer?: string;
    // Tertiary tokens
    tertiary?: string;
    onTertiary?: string;
    tertiaryContainer?: string;
    onTertiaryContainer?: string;
    // Error tokens
    errorContainer?: string;
    onErrorContainer?: string;
    // Outline tokens
    outline?: string;
    outlineVariant?: string;
    // Inverse tokens
    inverseSurface?: string;
    inverseOnSurface?: string;
    inversePrimary?: string;
    // Additional tokens
    shadow?: string;
    scrim?: string;
    onBackground?: string;
    // Fixed color tokens
    primaryFixed?: string;
    onPrimaryFixed?: string;
    primaryFixedDim?: string;
    onPrimaryFixedVariant?: string;
    secondaryFixed?: string;
    onSecondaryFixed?: string;
    secondaryFixedDim?: string;
    onSecondaryFixedVariant?: string;
    tertiaryFixed?: string;
    onTertiaryFixed?: string;
    tertiaryFixedDim?: string;
    onTertiaryFixedVariant?: string;
  }
}

// ---------------------------------------------------------------------------
// M3 scheme tokens — paste directly from Material Theme Builder JSON export
// (schemes.light / schemes.dark)
// ---------------------------------------------------------------------------
const m3Light = m3_palette.schemes.light;
const m3Dark = m3_palette.schemes.dark;


// ---------------------------------------------------------------------------
// M3 scheme → MUI palette mapping
// ---------------------------------------------------------------------------

type M3Scheme = Record<keyof typeof m3Light, string>;

function m3ToMuiPalette(scheme: M3Scheme, mode: 'light' | 'dark') {
  return {
    mode,
    primary: {
      main: scheme.primary,
      light: scheme.primaryContainer,
      dark: scheme.onPrimaryContainer,
      contrastText: scheme.onPrimary,
    },
    secondary: {
      main: scheme.secondary,
      light: scheme.secondaryContainer,
      dark: scheme.onSecondaryContainer,
      contrastText: scheme.onSecondary,
    },
    error: {
      main: scheme.error,
      light: scheme.errorContainer,
      dark: scheme.onErrorContainer,
      contrastText: scheme.onError,
    },
    background: {
      default: scheme.surface,
      paper: scheme.surfaceContainerLow,
    },
    text: {
      primary: scheme.onSurface,
      secondary: scheme.onSurfaceVariant,
    },
    divider: scheme.outlineVariant,
    // Surface containers
    surfaceContainerLowest: scheme.surfaceContainerLowest,
    surfaceContainerLow: scheme.surfaceContainerLow,
    surfaceContainer: scheme.surfaceContainer,
    surfaceContainerHigh: scheme.surfaceContainerHigh,
    surfaceContainerHighest: scheme.surfaceContainerHighest,
    surfaceDim: scheme.surfaceDim,
    surfaceBright: scheme.surfaceBright,
    surfaceTint: scheme.surfaceTint,
    surfaceVariant: scheme.surfaceVariant,
    onSurfaceVariant: scheme.onSurfaceVariant,
    // Primary tokens
    primaryContainer: scheme.primaryContainer,
    onPrimaryContainer: scheme.onPrimaryContainer,
    // Secondary tokens
    secondaryContainer: scheme.secondaryContainer,
    onSecondaryContainer: scheme.onSecondaryContainer,
    // Tertiary tokens
    tertiary: scheme.tertiary,
    onTertiary: scheme.onTertiary,
    tertiaryContainer: scheme.tertiaryContainer,
    onTertiaryContainer: scheme.onTertiaryContainer,
    // Error tokens
    errorContainer: scheme.errorContainer,
    onErrorContainer: scheme.onErrorContainer,
    // Outline tokens
    outline: scheme.outline,
    outlineVariant: scheme.outlineVariant,
    // Inverse tokens
    inverseSurface: scheme.inverseSurface,
    inverseOnSurface: scheme.inverseOnSurface,
    inversePrimary: scheme.inversePrimary,
    // Additional tokens
    shadow: scheme.shadow,
    scrim: scheme.scrim,
    onBackground: scheme.onBackground,
    // Fixed color tokens
    primaryFixed: scheme.primaryFixed,
    onPrimaryFixed: scheme.onPrimaryFixed,
    primaryFixedDim: scheme.primaryFixedDim,
    onPrimaryFixedVariant: scheme.onPrimaryFixedVariant,
    secondaryFixed: scheme.secondaryFixed,
    onSecondaryFixed: scheme.onSecondaryFixed,
    secondaryFixedDim: scheme.secondaryFixedDim,
    onSecondaryFixedVariant: scheme.onSecondaryFixedVariant,
    tertiaryFixed: scheme.tertiaryFixed,
    onTertiaryFixed: scheme.onTertiaryFixed,
    tertiaryFixedDim: scheme.tertiaryFixedDim,
    onTertiaryFixedVariant: scheme.onTertiaryFixedVariant,
  } as const;
}

// ---------------------------------------------------------------------------
// Shape (corner radius) scale
// ---------------------------------------------------------------------------

/**
 * COPA-DATA corner radius scale.
 *
 * We follow the M3 shape scale but with sharper corners than the M3 defaults
 * (M3: 4 / 8 / 12 / 16 / 28). Retune the whole app by editing these numbers —
 * every component override below and every `sx={{ borderRadius: n }}` (which
 * multiplies `shape.borderRadius`) derives from this scale.
 *
 * Deliberate outlier: the search bar keeps its own pill-like radius.
 */
export const cornerRadius = {
  none: 0,
  extraSmall: 2,
  small: 4,
  medium: 6,
  large: 8,
  extraLarge: 12,
  full: 9999,
} as const;

// ---------------------------------------------------------------------------
// Shared (non-color) theme options
// ---------------------------------------------------------------------------

const sharedThemeOptions: Omit<ThemeOptions, 'palette'> = {
  shape: {
    // Base unit for `sx={{ borderRadius: n }}` and for MUI components that
    // derive their radius from the theme (Paper, Menu, Dialog, inputs, ...).
    borderRadius: cornerRadius.small,
  },
  typography: {
    fontFamily: '"Segoe UI", "Roboto", "Helvetica", "Arial", sans-serif',
    h1: { fontSize: '2.25rem', fontWeight: 400, lineHeight: 1.2 },
    h2: { fontSize: '1.75rem', fontWeight: 400, lineHeight: 1.3 },
    h3: { fontSize: '1.5rem', fontWeight: 400, lineHeight: 1.3 },
    h4: { fontSize: '1.25rem', fontWeight: 400, lineHeight: 1.4 },
    h5: { fontSize: '1.125rem', fontWeight: 400, lineHeight: 1.4 },
    h6: { fontSize: '1rem', fontWeight: 500, lineHeight: 1.5 },
    subtitle1: { fontSize: '0.875rem', fontWeight: 500, lineHeight: 1.5 },
    body1: { fontSize: '1rem', fontWeight: 400, lineHeight: 1.5 },
    body2: { fontSize: '0.875rem', fontWeight: 400, lineHeight: 1.5 },
    button: { fontSize: '0.875rem', fontWeight: 500, textTransform: 'none', lineHeight: 1.5 },
    caption: { fontSize: '0.75rem', fontWeight: 400, lineHeight: 1.5 },
  },
  components: {
    MuiButton: {
      styleOverrides: {
        root: { borderRadius: cornerRadius.small, padding: '10px 24px' },
        contained: {
          boxShadow: 'none',
          '&:hover': { boxShadow: 'none' },
        },
      },
    },
    // MUI hardcodes a pill shape on Fab (50% circular, 24px extended).
    // Pull it back onto the scale so it matches the other buttons.
    MuiFab: {
      styleOverrides: {
        root: { borderRadius: cornerRadius.small },
      },
    },
    MuiCard: {
      styleOverrides: {
        root: { borderRadius: cornerRadius.medium, boxShadow: 'none' },
      },
    },
    MuiTextField: { defaultProps: { variant: 'outlined' } },
    MuiChip: { styleOverrides: { root: { borderRadius: cornerRadius.small } } },
    MuiPaper: { styleOverrides: { root: { backgroundImage: 'none' } } },
    MuiMenu: {
      styleOverrides: { paper: { borderRadius: cornerRadius.medium } },
    },
    MuiDialog: {
      styleOverrides: { paper: { borderRadius: cornerRadius.large } },
    },
    MuiTooltip: {
      styleOverrides: { tooltip: { borderRadius: cornerRadius.extraSmall } },
    },
    MuiAlert: {
      styleOverrides: { root: { borderRadius: cornerRadius.small } },
    },
  },
};

// ---------------------------------------------------------------------------
// Exports
// ---------------------------------------------------------------------------

export const lightTheme = createTheme({
  ...sharedThemeOptions,
  palette: m3ToMuiPalette(m3Light, 'light'),
});

export const darkTheme = createTheme({
  ...sharedThemeOptions,
  palette: m3ToMuiPalette(m3Dark, 'dark'),
});

export default lightTheme;

/** Raw M3 tokens for use in custom components that need tokens beyond MUI's palette. */
export { m3Light, m3Dark };
